import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
} from '@mui/material';
import { Folder as FolderIcon } from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import FormActionBar from '../../../shared/components/FormActionBar';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { usePortfolioManagement } from '../hooks/usePortfolioManagement';
import type { Portfolio } from '../services/catalogService';

interface PortfolioManagementPanelProps {
  isOpen: boolean;
  onCountChange?: (count: number) => void;
}

const PortfolioManagementPanel: React.FC<PortfolioManagementPanelProps> = ({ isOpen, onCountChange }) => {
  const { entityManagement } = usePortfolioManagement(isOpen);

  const {
    entities,
    selectedEntity,
    formData,
    saving,
    loading,
    error,
    successMessage,
    showDeleteConfirm,
    isNew,
    handleSelect,
    handleAddNew,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    setError,
    setSuccessMessage,
    hasChanges,
  } = entityManagement;

  useEffect(() => {
    onCountChange?.(entities.length);
  }, [entities.length, onCountChange]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, setSuccessMessage]);

  const showForm = !!selectedEntity || isNew;

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
      <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel<Portfolio>
          entities={entities}
          selectedEntity={selectedEntity}
          isNew={isNew}
          saving={saving}
          config={{
            getPrimaryText: (p) => p.display_name || p.name,
            getSecondaryText: (p) => p.description || 'No description',
            getId: (p) => p.id,
            Icon: FolderIcon,
          }}
          emptyStateText="No portfolios found"
          emptyStateHelperText=""
          onSelect={handleSelect}
          onAddNew={handleAddNew}
          addButtonText="Add Portfolio"
          isLoading={loading}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 3 }}>
        {error && (
          <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </InlineAlert>
        )}
        {successMessage && (
          <InlineAlert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </InlineAlert>
        )}

        {showForm ? (
          <>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Portfolio Details</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
                size="small"
                disabled={!isNew}
                helperText={!isNew ? 'Name cannot be changed after creation.' : undefined}
              />
              {!isNew && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                  Renaming this portfolio updates the display name only. The underlying AWS portfolio name is immutable.
                </Typography>
              )}
              <TextField
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => handleChange('display_name', e.target.value)}
                fullWidth
                size="small"
                placeholder="Human-readable label shown in UI"
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder="Purpose and contents of this portfolio"
              />


            </Box>

            <FormActionBar
              onSave={handleSave}
              saving={saving}
              saveDisabled={saving || !hasChanges()}
              onDelete={selectedEntity ? handleDeleteClick : undefined}
            />
          </>
        ) : (
          <EmptyStateMessage
            icon={FolderIcon}
            title="No portfolio selected"
            description="Select a portfolio to view its details. Portfolios are created by the service-catalog pipeline."
          />
        )}
      </Box>

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        title="Delete Portfolio?"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete Portfolio"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete: <strong>{selectedEntity?.display_name || selectedEntity?.name}</strong>
        </Typography>
        <InlineAlert severity="warning">
          <Typography variant="body2">
            This will fail if any products are still assigned to this portfolio. Reassign them first.
          </Typography>
        </InlineAlert>
      </DeleteConfirmDialog>
    </Box>
  );
};

export default PortfolioManagementPanel;
